import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import portalAPI from '../../services/portalAPI';
import { reportsAPI, triggerDownload } from '../../services/reportsAPI';
import PortalFooter from '../../components/Layout/PortalFooter';
import './ReportsBatches.css';

interface Batch {
  id: string;
  batch_number: string;
  custom_name: string | null;
  animal_type: string;
  animal_type_display: string;
  quantity_started: number;
  current_count: number;
  arrival_date: string;
  target_harvest_date: string | null;
  status: string;
  status_display: string;
}

const ReportsBatches: React.FC = () => {
  const [batches, setBatches] = useState<Batch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const [downloading, setDownloading] = useState<string | null>(null);

  useEffect(() => {
    loadBatches();
  }, [filter]);

  const loadBatches = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {};
      if (filter !== 'all') {
        params.status = filter;
      }
      const response = await portalAPI.get('/planner/portal/batches/', { params });
      setBatches(response.data.results || response.data || []);
    } catch (err: any) {
      console.error('Failed to load batches:', err);
      setError('Failed to load batches. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (batch: Batch) => {
    setDownloading(batch.id);
    try {
      const blob = await reportsAPI.downloadBatchReport(batch.id);
      const filename = `batch_report_${batch.batch_number}_${new Date().toISOString().split('T')[0]}.pdf`;
      triggerDownload(blob, filename);
    } catch (err: any) {
      console.error('Failed to download report:', err);
      alert('Failed to download report. Please try again.');
    } finally {
      setDownloading(null);
    }
  };

  const harvestedBatches = batches.filter(b => b.status === 'harvested' || b.status === 'completed');
  const activeBatches = batches.filter(b => b.status === 'active' || b.status === 'in_progress');

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="reports-batches">
      <div className="page-header">
        <Link to="/portal/reports" className="back-link">← Back to Reports</Link>
        <h1>Batch Reports</h1>
        <p>Download completion reports for your batches</p>
      </div>

      <div className="filters">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Batches</option>
          <option value="harvested">Harvested Only</option>
          <option value="active">Active Only</option>
          <option value="planned">Planned Only</option>
        </select>

        <span className="batch-count">
          {batches.length} batch{batches.length !== 1 ? 'es' : ''} found
        </span>
      </div>

      {loading ? (
        <div className="loading-state">Loading batches...</div>
      ) : error ? (
        <div className="error-state">
          {error}
          <button onClick={loadBatches} className="retry-btn">Retry</button>
        </div>
      ) : batches.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📭</div>
          <p>No batches found matching your filters.</p>
          <Link to="/portal/farmhand/create" className="create-btn">Create Your First Batch</Link>
        </div>
      ) : (
        <div className="batches-table-container">
          <table className="batches-table">
            <thead>
              <tr>
                <th>Batch</th>
                <th>Animal Type</th>
                <th>Head Count</th>
                <th>Dates</th>
                <th>Status</th>
                <th>Report</th>
              </tr>
            </thead>
            <tbody>
              {batches.map(batch => (
                <tr key={batch.id}>
                  <td className="batch-name">
                    <Link to={`/portal/reports/batches/${batch.id}`}>
                      {batch.custom_name || batch.batch_number}
                    </Link>
                  </td>
                  <td>{batch.animal_type_display || batch.animal_type}</td>
                  <td>{batch.current_count} / {batch.quantity_started}</td>
                  <td className="dates-cell">
                    <span>{formatDate(batch.arrival_date)}</span>
                    {batch.target_harvest_date && (
                      <span className="date-separator"> → {formatDate(batch.target_harvest_date)}</span>
                    )}
                  </td>
                  <td>
                    <span className={`status-badge status-${batch.status}`}>
                      {batch.status_display || batch.status}
                    </span>
                  </td>
                  <td>
                    {batch.status === 'harvested' || batch.status === 'completed' ? (
                      <button
                        onClick={() => handleDownload(batch)}
                        disabled={downloading === batch.id}
                        className="download-btn"
                      >
                        {downloading === batch.id ? 'Downloading...' : 'Download PDF'}
                      </button>
                    ) : (
                      <span className="not-available">Not yet harvested</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="info-box">
        <strong>Tip:</strong> Batch reports are available after a batch is marked as harvested.
        Reports include financial summary, production logs, weather impact, and performance metrics.
      </div>

      <PortalFooter />
    </div>
  );
};

export default ReportsBatches;
